// src/redux/profiles/profileSlice.ts

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import type { VisitorProfile } from "../../types/profiles/visitorProfileTypes";
import type { MemberProfileType } from "../../types/profiles/memberProfileTypes";
import type { GuestProfileType } from "../../types/profiles/guestProfileTypes";
import type { UpdateMemberProfileResponse } from "../../types/profiles/updateMemberProfileResponse";
import type { UpdateGuestProfileResponse } from "../../types/profiles/updateGuestProfileResponse";

import {
  fetchMemberProfileApi,
  updateMemberProfileApi,
  updateMemberProfileImageApi,
  requestEmailChangeApi,
  confirmEmailChangeApi,
  requestPhoneVerificationApi,
  verifyPhoneApi,
  removePhoneApi,
  deleteAcademicRecordApi,
  toggleVisibilityApi,
} from "../../services/profiles/memberProfileApi";

import {
  fetchGuestProfileApi,
  updateGuestProfileApi,
  updateGuestProfileImageApi,
} from "../../services/profiles/guestProfileApi";

import { fetchAuthMe } from "../auth/authSlice";

type ActiveProfile = MemberProfileType | GuestProfileType | null;

interface ProfileState {
  profile: ActiveProfile;
  loading: boolean;
  error: string | Record<string, any> | null;
  successMessage: string | null;
  emailChangeStatus: "idle" | "requested" | "confirmed" | "error";
  phoneVerificationStatus: "idle" | "pending" | "success" | "error";

  visitorProfile: VisitorProfile | null;
  visitorLoading: boolean;
  visitorError: string | Record<string, any> | null;
}

const initialState: ProfileState = {
  profile: null,
  loading: false,
  error: null,
  emailChangeStatus: "idle",
  phoneVerificationStatus: "idle",
  successMessage: null,

  visitorProfile: null,
  visitorLoading: false,
  visitorError: null,
};

// Member thunks ------------------------------------------------------------------
export const getMemberProfile = createAsyncThunk(
  "profile/getMemberProfile",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchMemberProfileApi();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "An error occurred while fetching the member profile."
      );
    }
  }
);

export const updateMemberProfile = createAsyncThunk<
  UpdateMemberProfileResponse,
  any,
  { rejectValue: string | Record<string, any> }
>(
  "profile/updateMemberProfile",
  async (updatedData, { rejectWithValue, dispatch }) => {
    try {
      const data = await updateMemberProfileApi(updatedData);
      await dispatch(fetchAuthMe()).unwrap();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update member profile."
      );
    }
  }
);

export const updateMemberProfileImage = createAsyncThunk(
  "profile/updateMemberProfileImage",
  async (formData: FormData, { rejectWithValue, dispatch }) => {
    try {
      const data = await updateMemberProfileImageApi(formData);
      await dispatch(fetchAuthMe()).unwrap();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update member profile image."
      );
    }
  }
);

// Guest thunks -------------------------------------------------------------------
export const getGuestProfile = createAsyncThunk(
  "profile/getGuestProfile",
  async (_, { rejectWithValue }) => {
    try {
      return await fetchGuestProfileApi();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "An error occurred while fetching the guest profile."
      );
    }
  }
);

export const updateGuestProfile = createAsyncThunk<
  UpdateGuestProfileResponse,
  any,
  { rejectValue: string | Record<string, any> }
>(
  "profile/updateGuestProfile",
  async (updatedData, { rejectWithValue, dispatch }) => {
    try {
      const data = await updateGuestProfileApi(updatedData);
      await dispatch(fetchAuthMe()).unwrap();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update guest profile."
      );
    }
  }
);

export const updateGuestProfileImage = createAsyncThunk(
  "profile/updateGuestProfileImage",
  async (formData: FormData, { rejectWithValue, dispatch }) => {
    try {
      const data = await updateGuestProfileImageApi(formData);
      await dispatch(fetchAuthMe()).unwrap();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "Failed to update guest profile image."
      );
    }
  }
);

// Member-only thunks -------------------------------------------------------------
export const requestEmailChange = createAsyncThunk(
  "profile/requestEmailChange",
  async (emailData: { new_email: string }, { rejectWithValue }) => {
    try {
      return await requestEmailChangeApi(emailData);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Failed to request email change."
      );
    }
  }
);

export const confirmEmailChange = createAsyncThunk(
  "profile/confirmEmailChange",
  async (
    confirmationData: { old_email_code: string; new_email_code: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const data = await confirmEmailChangeApi(confirmationData);
      await dispatch(getMemberProfile()).unwrap();
      await dispatch(fetchAuthMe()).unwrap();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Failed to confirm email change."
      );
    }
  }
);

export const requestPhoneVerification = createAsyncThunk<
  any,
  { phone_number: string },
  { rejectValue: { error: string } }
>(
  "profile/requestPhoneVerification",
  async (phoneData, { rejectWithValue }) => {
    try {
      return await requestPhoneVerificationApi(phoneData);
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || { error: "Something went wrong" }
      );
    }
  }
);

export const verifyPhone = createAsyncThunk<
  any,
  { phone_number: string; activation_code: string },
  { rejectValue: string | Record<string, any> }
>(
  "profile/verifyPhone",
  async (verificationData, { rejectWithValue, dispatch }) => {
    try {
      const data = await verifyPhoneApi(verificationData);
      await dispatch(fetchAuthMe()).unwrap();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "An error occurred while verifying phone."
      );
    }
  }
);

export const removePhone = createAsyncThunk(
  "profile/removePhone",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const data = await removePhoneApi();
      await dispatch(fetchAuthMe()).unwrap();
      return data;
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data || "An error occurred while removing phone."
      );
    }
  }
);

export const deleteAcademicRecord = createAsyncThunk<
  { message: string },
  void,
  { rejectValue: string }
>(
  "profile/deleteAcademicRecord",
  async (_, { rejectWithValue }) => {
    try {
      return await deleteAcademicRecordApi();
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Failed to delete academic record."
      );
    }
  }
);

export const toggleVisibility = createAsyncThunk<
  any,
  { memberId: number; is_hidden_by_confidants: boolean },
  { rejectValue: string }
>(
  "fellowship/toggleVisibility",
  async ({ memberId, is_hidden_by_confidants }, { rejectWithValue }) => {
    try {
      return await toggleVisibilityApi({ memberId, is_hidden_by_confidants });
    } catch (err: any) {
      return rejectWithValue(
        err.response?.data?.error || "Failed to toggle visibility."
      );
    }
  }
);

// Slice -------------------------------------------------------------------------
const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    clearProfile(state) {
      state.profile = null;
      state.loading = false;
      state.error = null;
      state.successMessage = null;
      state.emailChangeStatus = "idle";
      state.phoneVerificationStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Member fetch ------------------------------------------------------
      .addCase(getMemberProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getMemberProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(getMemberProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.successMessage = null;
      })

      // Guest fetch -------------------------------------------------------
      .addCase(getGuestProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getGuestProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload;
      })
      .addCase(getGuestProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.successMessage = null;
      })

      // Member update -----------------------------------------------------
      .addCase(updateMemberProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMemberProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.member;
        state.successMessage = action.payload.message;
      })
      .addCase(updateMemberProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Guest update ------------------------------------------------------
      .addCase(updateGuestProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGuestProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.profile = action.payload.guest;
        state.successMessage = action.payload.message;
      })
      .addCase(updateGuestProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Member image update -----------------------------------------------
      .addCase(updateMemberProfileImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMemberProfileImage.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.message || "Profile image updated successfully.";
        if (action.payload.member) {
          state.profile = action.payload.member;
        }
      })
      .addCase(updateMemberProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Guest image update ------------------------------------------------
      .addCase(updateGuestProfileImage.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateGuestProfileImage.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.message || "Profile image updated successfully.";
        if (action.payload.guest) {
          state.profile = action.payload.guest;
        }
      })
      .addCase(updateGuestProfileImage.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Request email change ----------------------------------------------
      .addCase(requestEmailChange.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
        state.emailChangeStatus = "idle";
      })
      .addCase(requestEmailChange.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.emailChangeStatus = "requested";
      })
      .addCase(requestEmailChange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.emailChangeStatus = "error";
      })

      // Confirm email change ----------------------------------------------
      .addCase(confirmEmailChange.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(confirmEmailChange.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.emailChangeStatus = "confirmed";
      })
      .addCase(confirmEmailChange.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Request phone verification ----------------------------------------
      .addCase(requestPhoneVerification.pending, (state) => {
        state.phoneVerificationStatus = "pending";
      })
      .addCase(requestPhoneVerification.fulfilled, (state, action) => {
        state.phoneVerificationStatus = "success";
        state.successMessage = action.payload.message || null;
      })
      .addCase(requestPhoneVerification.rejected, (state, action) => {
        state.phoneVerificationStatus = "error";
        state.error =
          typeof action.payload === "string"
            ? action.payload
            : action.payload?.error || "Something went wrong";
      })

      // Verify phone ------------------------------------------------------
      .addCase(verifyPhone.pending, (state) => {
        state.phoneVerificationStatus = "pending";
      })
      .addCase(verifyPhone.fulfilled, (state, action) => {
        state.phoneVerificationStatus = "success";
        state.successMessage = action.payload.message || null;
      })
      .addCase(verifyPhone.rejected, (state, action) => {
        state.phoneVerificationStatus = "error";
        state.error = action.payload as string;
      })

      // Remove phone ------------------------------------------------------
      .addCase(removePhone.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removePhone.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || null;
      })
      .addCase(removePhone.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Delete academic record --------------------------------------------
      .addCase(deleteAcademicRecord.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAcademicRecord.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;

        if (
          state.profile &&
          "academic_record" in state.profile
        ) {
          state.profile.academic_record = null;
        }
      })
      .addCase(deleteAcademicRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Toggle visibility -------------------------------------------------
      .addCase(toggleVisibility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleVisibility.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload.message || "Visibility toggled successfully.";
      })
      .addCase(toggleVisibility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to toggle visibility.";
      });
  },
});

export const { clearProfile } = profileSlice.actions;
export default profileSlice.reducer;